import type { RuleSet } from '../rules';
import { buildingLevel } from './levels';
import type { Building } from './types';

/**
 * Per-city worker targets (the `setWorkTargets` command, `Building.workTargets`):
 * how many workers a city keeps on each task. A task is a resource id (gather
 * it) or `BUILD_TASK` (build the city's sites). Automation counts pinned and
 * auto workers toward them and fills what is short from the auto ones
 * (systems/automation/worker.ts `pickWork`).
 */

/** The task id for building sites. */
const BUILD_TASK = 'build';
/** Most tasks one `workTargets` may name; bounds the wire. */
const MAX_TARGET_TASKS = 16;
/** Most one task's target may be on the wire; the sim clamps to the city's worker cap. */
const MAX_WORK_TARGET = 1000;

/** Whether `task` is a task id of `rules`: a resource or `BUILD_TASK`. */
function isTask(rules: RuleSet, task: string): boolean {
  return task === BUILD_TASK || Object.hasOwn(rules.resources, task);
}

/**
 * How many workers `city` supports: the worker type's `perCity` plus
 * `perCityLevel` per level above 1 (the unit cap's per-city share, see
 * systems/spawn.ts `unitCap`). A rules set without a worker cap allows
 * `MAX_WORK_TARGET`.
 */
function cityWorkerCap(rules: RuleSet, city: Building): number {
  for (const def of Object.values(rules.units)) {
    if (def.automate !== 'work' || def.capAs !== undefined || def.perCity === undefined) continue;
    return def.perCity + (def.perCityLevel ?? 0) * (buildingLevel(city) - 1);
  }
  return MAX_WORK_TARGET;
}

/**
 * `targets` cleaned for `city`: unknown tasks and zero entries dropped, each
 * value and then the sum clamped to the city's worker cap (in key order, so
 * a UI must keep the sum within `cityWorkerCap` itself, or later tasks lose).
 * Undefined when nothing is left.
 */
function cleanTargets(
  rules: RuleSet,
  city: Building,
  targets: Record<string, number>
): Record<string, number> | undefined {
  const cap = cityWorkerCap(rules, city);
  const out: Record<string, number> = {};
  let left = cap;
  for (const [task, n] of Object.entries(targets)) {
    if (!isTask(rules, task) || !Number.isSafeInteger(n)) continue;
    const take = Math.min(Math.max(0, n), left);
    if (take <= 0) continue;
    out[task] = take;
    left -= take;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export { BUILD_TASK, cityWorkerCap, cleanTargets, isTask, MAX_TARGET_TASKS, MAX_WORK_TARGET };
